import { createContext, useContext, useMemo, ReactNode } from 'react';
import { usePermissions } from './PermissionContext';
import { SCREEN_ACCESS, decideScreen } from '@/lib/route-permissions';

/**
 * Navigation Context - Single Responsibility: UI navigation helpers
 * Depends on PermissionContext for role-based routing.
 *
 * Screen visibility derives from route-permissions.SCREEN_ACCESS — the single
 * role → screen map shared with the router guards (2026-07-13 UX audit C-9;
 * previously this file kept its own copy that had drifted) — through
 * `decideScreen`, which answers a grant holder by its grant once `/access/me`
 * says it holds one (#1645); until then, by the role, as before.
 */

interface NavigationContextValue {
  getLandingPage: () => string;
  getVisibleScreens: () => string[];
}

const NavigationContext = createContext<NavigationContextValue | null>(null);

export function NavigationProvider({ children }: { children: ReactNode }) {
  const { globalRole, isOwner, access } = usePermissions();

  const getLandingPage = useMemo(() => {
    return () => {
      // All roles now have dashboard access
      // Supervisor and Agent get their role-specific dashboards
      switch (globalRole) {
        case 'supervisor':
          return '/dashboard'; // SupervisorDashboard with Team Performance
        case 'agent':
          return '/dashboard'; // AgentDashboard with My Performance
        default:
          return '/dashboard';
      }
    };
  }, [globalRole]);

  const getVisibleScreens = useMemo(() => {
    return () => {
      // Platform owners (super-admins) can see all screens plus the admin area
      // ('admin' is gated by isOwner, so it is not part of SCREEN_ACCESS).
      if (isOwner) {
        return [...Object.keys(SCREEN_ACCESS), 'admin'];
      }

      if (!globalRole) return [];

      return Object.keys(SCREEN_ACCESS).filter(
        (screen) => decideScreen(screen, { globalRole, isOwner, access }) === 'allowed'
      );
    };
  }, [globalRole, isOwner, access]);

  return (
    <NavigationContext.Provider value={{ getLandingPage, getVisibleScreens }}>
      {children}
    </NavigationContext.Provider>
  );
}

export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (!context) throw new Error('useNavigation must be used within NavigationProvider');
  return context;
};
